import React from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  CircularProgress,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  GraphicEq as AudioIcon,
  Schedule as ScheduleIcon,
  Bolt as BoltIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  JobTranscricao,
  StatusArquivo,
  formatarDuracao,
  formatarTempoRestante,
  nomeLote,
} from '../services/transcricao';
import { colors } from '../theme/expertiseTheme';

/**
 * Acompanhamento do processamento de uma pasta em transcrição
 */
type Props = {
  job: JobTranscricao;
  onCancelar: () => void;
  cancelando: boolean;
};

const statusArquivo: Record<StatusArquivo, { label: string; cor: string; bgColor: string }> = {
  Pendente: {
    label: 'Na Fila',
    cor: colors.text.secondary,
    bgColor: colors.light,
  },
  Processando: {
    label: 'Processando',
    cor: colors.info,
    bgColor: 'rgba(59, 130, 246, 0.1)',
  },
  Concluido: {
    label: 'Concluído',
    cor: colors.primary,
    bgColor: 'rgba(0, 99, 0, 0.1)',
  },
  Erro: {
    label: 'Erro',
    cor: colors.error,
    bgColor: 'rgba(220, 38, 38, 0.1)',
  },
};

/**
 * Progresso individual de cada arquivo, considerando o status quando o
 * backend não envia o percentual
 */
const progressoArquivo = (status: StatusArquivo, progresso?: number): number => {
  if (status === 'Concluido') return 100;
  if (status === 'Pendente' || status === 'Erro') return 0;
  return Math.min(100, Math.max(0, progresso || 0));
};

const LINHAS_CARREGANDO = 3;

const TranscricaoProgresso: React.FC<Props> = ({ job, onCancelar, cancelando }) => {
  const progresso = Math.min(100, Math.max(0, Math.round(job.progresso || 0)));
  const listandoPasta = job.status === 'Pendente';
  const arquivos = job.arquivos || [];

  return (
    <Box>
      {/* Resumo do lote */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 3,
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
        }}
      >
        {/* Anel de progresso */}
        <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={120}
            thickness={4}
            sx={{ color: colors.border }}
          />
          {listandoPasta ? (
            <CircularProgress
              size={120}
              thickness={4}
              sx={{ color: colors.primary, position: 'absolute', left: 0 }}
            />
          ) : (
            <>
              <CircularProgress
                variant="determinate"
                value={progresso}
                size={120}
                thickness={4}
                sx={{
                  color: colors.primary,
                  position: 'absolute',
                  left: 0,
                  '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: colors.dark, lineHeight: 1.1 }}
                >
                  {progresso}%
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    color: colors.text.secondary,
                  }}
                >
                  CONCLUÍDO
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* Informações do lote */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: colors.dark, fontSize: { xs: '1.25rem', md: '1.5rem' } }}
          >
            {listandoPasta ? (
              'Listando arquivos da pasta…'
            ) : (
              <>
                {job.arquivosConcluidos} de {job.totalArquivos} arquivo
                {job.totalArquivos !== 1 ? 's' : ''} concluído
                {job.arquivosConcluidos !== 1 ? 's' : ''}
              </>
            )}
          </Typography>

          <Typography variant="body2" sx={{ color: colors.text.secondary, mt: 0.5 }}>
            Lote: {nomeLote(job)}
          </Typography>

          {listandoPasta ? (
            <Typography variant="body2" sx={{ color: colors.text.secondary, mt: 2 }}>
              Os arquivos de áudio aparecem aqui assim que a listagem terminar.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: { xs: 1.5, md: 3 },
                mt: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon sx={{ fontSize: 18, color: colors.primary }} />
                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                  Tempo estimado restante:{' '}
                  <Box component="strong" sx={{ color: colors.dark }}>
                    {formatarTempoRestante(job.tempoRestanteSegundos)}
                  </Box>
                </Typography>
              </Box>

              {job.velocidade && (
                <>
                  <Box
                    sx={{
                      width: '1px',
                      height: 20,
                      backgroundColor: colors.border,
                      display: { xs: 'none', md: 'block' },
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BoltIcon sx={{ fontSize: 18, color: colors.primary }} />
                    <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                      Velocidade de Processamento:{' '}
                      <Box component="strong" sx={{ color: colors.dark }}>
                        {job.velocidade}
                      </Box>
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>

        <Button
          variant="outlined"
          color="primary"
          onClick={onCancelar}
          disabled={cancelando}
          startIcon={<CloseIcon />}
          sx={{ flexShrink: 0 }}
        >
          {cancelando ? 'Cancelando...' : 'Cancelar'}
        </Button>
      </Paper>

      {/* Lista de arquivos */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome do arquivo</TableCell>
                <TableCell>Duração</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ minWidth: 260 }}>Progresso individual</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listandoPasta &&
                arquivos.length === 0 &&
                Array.from({ length: LINHAS_CARREGANDO }, (_, indice) => (
                  <TableRow key={`carregando-${indice}`}>
                    <TableCell>
                      <Skeleton width="70%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={48} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={80} />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                  </TableRow>
                ))}
              {arquivos.map((arquivo) => {
                const config = statusArquivo[arquivo.status] || statusArquivo.Pendente;
                const valor = progressoArquivo(arquivo.status, arquivo.progresso);

                return (
                  <TableRow key={arquivo.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AudioIcon sx={{ fontSize: 22, color: colors.text.light, flexShrink: 0 }} />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: colors.dark }}
                        >
                          {arquivo.nome}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                        {formatarDuracao(arquivo.duracaoSegundos)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={arquivo.erro || ''} disableHoverListener={!arquivo.erro}>
                        <Chip
                          label={config.label}
                          size="small"
                          sx={{
                            backgroundColor: config.bgColor,
                            color: config.cor,
                            fontWeight: 500,
                          }}
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={valor}
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: colors.light,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor:
                                arquivo.status === 'Erro' ? colors.error : colors.primary,
                              borderRadius: 4,
                            },
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            minWidth: 44,
                            textAlign: 'right',
                          }}
                        >
                          {valor}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography
        variant="body2"
        sx={{ color: colors.text.light, textAlign: 'center', mt: 3 }}
      >
        Você pode continuar usando o sistema. O processamento segue em execução no servidor.
      </Typography>
    </Box>
  );
};

export default TranscricaoProgresso;
